import { useCallback, useMemo, useReducer, useContext, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { noop } from "lodash";
import moment from "moment-timezone";
import useApi from "../../../utils/api";
import { initialState, reducer } from "../../Settings/hook/reducer";
import { ProfileContext } from "../context";
import { useCompany, useCompanyDateFormat } from "../../../utils/hooks/company";
import { getErrorMessage } from "../../../utils/helpers/errors";

export const useRequestTimeOffApi = () => {
	const { t } = useTranslation();
	const { authGet, authPost, authDelete, authPut } = useApi();

	const company = useCompany();
	const dateFormat = useCompanyDateFormat() || "MM/DD/YYYY";
	const { user } = useContext(ProfileContext);

	const [state, dispatch] = useReducer(reducer, initialState);

	const selected = useMemo(() => {
		const data = state.data;
		const selectedId = state.selectedId;
		return selectedId !== null && data !== null
			? data.find((resource) => resource.id === selectedId)
			: undefined;
	}, [state.data, state.selectedId]);

	const setMode = useCallback(
		(mode, selectedId = null) =>
			dispatch({
				type: "changeMode",
				payload: { mode, selectedId },
			}),
		[dispatch],
	);

	const goToView = useCallback(() => setMode("view"), [setMode]);

	const goToCreate = useCallback(() => setMode("create"), [setMode]);

	const goToEdit = useCallback((id) => setMode("edit", id), [setMode]);

	const goToClone = useCallback((id) => setMode("clone", id), [setMode]);

	const goToTransfer = useCallback((id) => setMode("transfer", id), [setMode]);

	const goToLedger = useCallback((id) => setMode("ledger", id), [setMode]);
	const goToPayout = useCallback((id) => setMode("payout", id), [setMode]);
	const goToPayoutAll = useCallback(() => setMode("payout-all"), [setMode]);

	const fetch = useCallback(
		async (controller) => {
			dispatch({ type: "requestFetch" });

			try {
				const response = await authGet(`/users/${user?.id}/time-off`, {
					signal: controller?.signal,
				});

				if (response) {
					dispatch({
						type: "fetchSucceeded",
						payload: {
							result: response,
							totalItems: response.length,
						},
					});
				}
			} catch (err) {
				dispatch({ type: "fetchFailed", payload: err });
				if (typeof err === "string") {
					toast.error(err);
				}
			}
		},
		[authGet, user?.id, dispatch],
	);

	const create = useCallback(
		async (data, onSuccess = noop, onFailure = noop) => {
			dispatch({ type: "requestCreate" });
			try {
				const response = await authPost(`/users/${user?.id}/time-off`, {
					data: { ...data, company: company?.id },
				});
				if (response) {
					dispatch({
						type: "createSucceeded",
						payload: response,
					});
					onSuccess(response);
					toast.success(t("resource-created"));
					goToView();
				}
			} catch (err) {
				dispatch({
					type: "createFailed",
					payload: err,
				});
				onFailure(err);

				if (typeof err === "string") {
					toast.error(err);
				}
			}
		},
		[authPost, user?.id, company?.id, t, goToView],
	);

	const update = useCallback(
		async (id, data, onSuccess = noop, onFailure = noop) => {
			dispatch({ type: "requestUpdate" });
			if(data.addOrRemove === "add") {
				data.amount = 1 *data.amount;
			} else {
				data.amount = -1 * data.amount;
			}
			delete data.addOrRemove;

			try {
				const response = await authPut(
					`/users/${user?.id}/time-off/${id}`,
					{ data: { ...data, company: company?.id } },
				);
				if (response) {
					dispatch({
						type: "updateSucceeded",
						payload: response,
					});
					onSuccess(response);
					toast.success(t("resource-updated"));
					goToView();
				}
			} catch (err) {
				dispatch({ type: "updateFailed", payload: err });
				onFailure(err);
				getErrorMessage(err, t);
			}
		},
		[authPut, user?.id, company?.id, t, goToView],
	);

	const transfer = useCallback(async (timeoffId, data, onSuccess = noop, onFailure = noop) => {
		try {
			const response = await authPost(`/users/${user?.id}/time-off/transfer/${timeoffId}`, { data })
			if(response) {
				dispatch({
					type: "transferSucceeded",
					payload: response,
				});
				onSuccess(response);
				toast.success(t("resource-transferred-successfully"));
				goToView();
			}
		} catch(error) {
			dispatch({ type: "transferFailed", payload: error });
			onFailure(error)
			getErrorMessage(error, t)
		}
	}, [authPost, goToView, t, user?.id])

	// assign / transfer as of a past effective date (settings PtoEffectiveDateService):
	// returns the preview, writes only with apply = true
	const effective = useCallback(async (mode, timeoffId, data, apply = false) => {
		const url = mode === "transfer"
			? `/time-off-plan/${user?.id}/effective/transfer/${timeoffId}`
			: `/time-off-plan/${user?.id}/effective/assign`;
		try {
			const response = await authPost(url, { data: { ...data, apply } });
			if (response && apply) {
				toast.success(t(mode === "transfer" ? "resource-transferred-successfully" : "resource-created"));
				goToView();
				fetch();
			}
			return response;
		} catch (error) {
			getErrorMessage(error, t);
			return undefined;
		}
	}, [authPost, goToView, fetch, t, user?.id]);

	// future-dated assign / transfer (settings PtoScheduledChangeService): saved and applied on its
	// date by a daily job; listed on the page with a Cancel action
	const [scheduled, setScheduled] = useState([]);

	const fetchScheduled = useCallback(async (controller) => {
		try {
			const response = await authGet(`/time-off-plan/${user?.id}/scheduled-changes`, { signal: controller?.signal });
			setScheduled(Array.isArray(response) ? response : []);
		} catch (error) {
			// the plans stay usable without the list
			setScheduled([]);
		}
	}, [authGet, user?.id]);

	const schedule = useCallback(async (mode, timeoffId, data) => {
		try {
			const response = await authPost(`/time-off-plan/${user?.id}/scheduled-changes`, {
				data: {
					mode: mode === "transfer" ? "transfer" : "assign",
					userTimeOffPlan: mode === "transfer" ? timeoffId : undefined,
					...data,
				},
			});
			if (response) {
				toast.success(t("scheduled-change-created", { date: moment(response.effectiveDate).format(dateFormat) }));
				goToView();
				fetchScheduled();
			}
			return response;
		} catch (error) {
			getErrorMessage(error, t);
			return undefined;
		}
	}, [authPost, goToView, fetchScheduled, t, user?.id, dateFormat]);

	const cancelScheduled = useCallback(async (id) => {
		try {
			await authPost(`/time-off-plan/${user?.id}/scheduled-changes/${id}/cancel`);
			toast.success(t("scheduled-change-cancelled"));
		} catch (error) {
			getErrorMessage(error, t);
		}
		fetchScheduled();
	}, [authPost, fetchScheduled, t, user?.id]);
	// PTO payout (settings PtoPayoutService): what the plans pay out on the date, nothing written
	const previewPayout = useCallback(async (data) => {
		try {
			return await authPost(`/time-off-plan/${user?.id}/payout/preview`, { data });
		} catch (error) {
			getErrorMessage(error, t);
			return undefined;
		}
	}, [authPost, t, user?.id]);

	// pays out now (clock-in writes the time sheets, so the balances follow a moment later) or
	// schedules a later date
	const payout = useCallback(async (data) => {
		dispatch({ type: "requestUpdate" });
		try {
			const response = await authPost(`/time-off-plan/${user?.id}/payout`, { data });
			// nothing to put in the list yet: the payout time sheets come from clock-in
			dispatch({ type: "setState", payload: { submitting: false } });
			if (response?.scheduled?.length) {
				toast.success(t("scheduled-payout-created", { date: moment(response.preview?.date).format(dateFormat) }));
				fetchScheduled();
			} else {
				toast.success(t("resource-paid-out-successfully"));
				setTimeout(() => fetch(), 3000);
			}
			goToView();
		} catch (error) {
			dispatch({ type: "updateFailed", payload: error });
			getErrorMessage(error, t);
		}
	}, [authPost, fetch, fetchScheduled, goToView, t, user?.id, dateFormat]);

	const remove = useCallback(
		async (id, onSuccess = noop, onFailure = noop) => {
			const loading = toast("deleting-resouce", {
				autoClose: false,
			});

			try {
				await authDelete(`/users/${user?.id}/time-off/${id}`);
				dispatch({ type: "delete", payload: id });
				onSuccess(id);
				toast.dismiss(loading);
				toast.success(t("resource-deleted"));
			} catch (err) {
				onFailure(err);
				toast.dismiss(loading);
				getErrorMessage(err, t);
			}
		},
		[authDelete, user?.id, t],
	);

	return {
		state,
		fetch,
		create,
		update,
		remove,
		goToClone,
		goToCreate,
		goToEdit,
		goToView,
		selected,
		goToTransfer,
		goToLedger,
		transfer,
		effective,
		scheduled,
		fetchScheduled,
		schedule,
		cancelScheduled,
		goToPayout,
		goToPayoutAll,
		previewPayout,
		payout,
	};
};
